import React, { useState, useRef, useEffect } from 'react';
import { X, Save, Upload, Trash2, Info, Plus, Star, Sparkles, Loader2, Wand2, Megaphone, Car } from 'lucide-react';
import { carsysApi } from '../api/carsysApi';
import { useT } from '../context/LanguageContext';
import { useData } from '../context/DataContext';
import PartAdvertentie from './PartAdvertentie';
import { toast } from 'sonner';
import { makeDirtyClose } from '../lib/useDirtyClose';

const CATEGORIEEN = ['Verlichting', 'Carrosserie', 'Motor', 'Motor / elektrisch', 'Transmissie', 'Remsysteem', 'Ophanging', 'Stuurinrichting', 'Koeling', 'Wielen / banden', 'Interieur', 'Uitlaat', 'Overig'];
const CONDITIES = ['Nieuw', 'Zo goed als nieuw', 'Gebruikt', 'Refurbished', 'Defect / voor onderdelen'];
const RETOUR_STATUSSEN = ['Geen', 'Aangevraagd', 'Retour ontvangen', 'Terugbetaald'];

const verkleinFoto = (file) => new Promise((resolve) => {
  const reader = new FileReader();
  reader.onload = (e) => {
    const img = new Image();
    img.onload = () => {
      const max = 1280; let { width, height } = img;
      if (width > max || height > max) { const r = Math.min(max / width, max / height); width = Math.round(width * r); height = Math.round(height * r); }
      const c = document.createElement('canvas'); c.width = width; c.height = height;
      c.getContext('2d').drawImage(img, 0, 0, width, height);
      resolve(c.toDataURL('image/jpeg', 0.72));
    };
    img.src = e.target.result;
  };
  reader.readAsDataURL(file);
});

export default function PartEditor({ part, onClose, onSaved }) {
  const { t } = useT();
  const { gegevens } = useData();
  const locaties = gegevens?.locaties || [];
  const [tab, setTab] = useState('gegevens');
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const [d, setD] = useState({
    naam: '', categorie: '', oemNummer: '', onderdeelNummer: '', merk: '', conditie: 'Gebruikt',
    magazijnLocatie: '', aantal: 1, minVoorraad: 0, inkoopPrijs: '', vraagPrijs: '', verkoopPrijs: '',
    inkoopbron: '', garantieMaanden: 0, retourStatus: 'Geen', oemCrossRef: [], pastOp: [],
    opmerkingen: '', demontageDatum: '', afkomstigVoertuig: '', fotos: [], status: 'Voorraad',
    ...(part || {}),
  });
  // Niet-opgeslagen wijzigingen bewaken bij sluiten (FB-stijl)
  const sig = (x) => JSON.stringify({ ...x, fotos: (x.fotos || []).length });
  const initialSig = useRef(null);
  if (initialSig.current === null) initialSig.current = sig(d);
  const requestClose = makeDirtyClose(() => sig(d) !== initialSig.current, onClose, t);
  // AI prijsadvies (marktconformiteit van de vraagprijs)
  const [prijsBusy, setPrijsBusy] = useState(false);
  const [prijsAdvies, setPrijsAdvies] = useState(null);
  const haalPrijsAdvies = async () => {
    if (!(d.naam || '').trim()) { toast.error(t('Vul eerst een onderdeelnaam in', 'Vul eerst een onderdeelnaam in')); return; }
    setPrijsBusy(true);
    try {
      const r = await carsysApi.partWaardebepaling({
        naam: d.naam, conditie: d.conditie, oemNummer: d.oemNummer, merk: d.merk, model: d.model,
        bouwjaar: d.bouwjaar, motor: d.motor, kmStand: d.kmStand, afkomstigVoertuig: d.afkomstigVoertuig,
        vraagPrijs: Number(d.vraagPrijs) || Number(d.verkoopPrijs) || 0,
      });
      setPrijsAdvies(r);
    } catch (e) {
      toast.error(t('Prijsadvies mislukt', 'Prijsadvies mislukt'), { description: e.response?.data?.detail || e.message });
    } finally { setPrijsBusy(false); }
  };
  const gebruikAdviesprijs = () => {
    const p = prijsAdvies?.adviesVraagprijs;
    if (p) { upd('vraagPrijs', p); if (!(Number(d.verkoopPrijs) > 0)) upd('verkoopPrijs', p); toast.success(t('Adviesprijs overgenomen', 'Adviesprijs overgenomen')); }
  };

  const upd = (k, v) => setD(p => ({ ...p, [k]: v }));

  // Sloopauto's (donorvoertuig koppeling)
  const [sloopautos, setSloopautos] = useState([]);
  useEffect(() => {
    carsysApi.getSloopautos().then(setSloopautos).catch(() => {});
  }, []);
  const koppelSloopauto = (id) => {
    if (!id) { upd('sloopautoId', ''); return; }
    const s = sloopautos.find(x => x.id === id);
    if (!s) return;
    const voertuig = [s.merk, s.model].filter(Boolean).join(' ').trim();
    setD(p => ({
      ...p,
      sloopautoId: s.id,
      afkomstigVoertuig: voertuig || s.kenteken || p.afkomstigVoertuig,
      kenteken: p.kenteken || s.kenteken || '',
      merk: p.merk || s.merk || '',
      bouwjaar: p.bouwjaar || (s.bouwjaar ? String(s.bouwjaar) : ''),
      kmStand: (Number(p.kmStand) > 0 ? p.kmStand : (s.kmStand || '')),
      motor: p.motor || s.motor || '',
      inkoopbron: p.inkoopbron || (s.kenteken ? `Sloopauto ${s.kenteken}` : p.inkoopbron),
    }));
    toast.success(t('Sloopauto gekoppeld', 'Sloopauto gekoppeld'));
  };

  // AI: herken onderdeel uit foto's
  const [fotoBusy, setFotoBusy] = useState(false);
  const fotoAnalyse = async () => {
    if (!(d.fotos || []).length) { toast.error(t('Upload eerst minimaal één foto', 'Upload eerst minimaal één foto')); return; }
    setFotoBusy(true);
    try {
      const r = await carsysApi.partFotoAnalyse({
        fotos: d.fotos, merk: d.merk, afkomstigVoertuig: d.afkomstigVoertuig, bouwjaar: d.bouwjaar, motor: d.motor,
      });
      setD(p => {
        const next = { ...p };
        const fill = (k, v) => { if (v != null && String(v).trim() !== '' && (next[k] == null || String(next[k]).trim() === '')) next[k] = v; };
        fill('naam', r.naam); fill('categorie', r.categorie); fill('merk', r.merk);
        fill('onderdeelNummer', r.onderdeelNummer); fill('oemNummer', r.oemNummer);
        if (r.conditie) next.conditie = r.conditie;
        if (r.geschatteVerkoopprijs && !(Number(next.verkoopPrijs) > 0)) { next.verkoopPrijs = r.geschatteVerkoopprijs; if (!(Number(next.vraagPrijs) > 0)) next.vraagPrijs = r.geschatteVerkoopprijs; }
        const codes = (r.zichtbareCodes || []).filter(c => c && ![next.oemNummer, next.onderdeelNummer].includes(c));
        if (codes.length) next.oemCrossRef = Array.from(new Set([...(next.oemCrossRef || []), ...codes]));
        if (r.beschrijving && !(next.opmerkingen || '').trim()) next.opmerkingen = r.beschrijving;
        return next;
      });
      toast[(r.naam || r.categorie) ? 'success' : 'info'](
        (r.naam || r.categorie)
          ? t('Onderdeel herkend — gegevens ingevuld', 'Onderdeel herkend — gegevens ingevuld')
          : t('Kon het onderdeel niet goed herkennen — vul handmatig aan', 'Kon het onderdeel niet goed herkennen — vul handmatig aan'),
        { description: r.zekerheid ? `${t('Zekerheid', 'Zekerheid')}: ${r.zekerheid}` : undefined }
      );
      setTab('gegevens');
    } catch (e) {
      toast.error(t('Fotoanalyse mislukt', 'Fotoanalyse mislukt'), { description: e.response?.data?.detail || e.message });
    } finally { setFotoBusy(false); }
  };

  // Advertentie maken: open de AI-modal ZONDER op te slaan (genereren werkt op de ingevoerde gegevens)
  const [adPart, setAdPart] = useState(null);
  const [savedId, setSavedId] = useState(part?.id || null);

  const _payload = () => ({ ...d, aantal: Number(d.aantal) || 1, minVoorraad: Number(d.minVoorraad) || 0, garantieMaanden: Number(d.garantieMaanden) || 0, inkoopPrijs: Number(d.inkoopPrijs) || 0, vraagPrijs: Number(d.vraagPrijs) || 0, verkoopPrijs: Number(d.verkoopPrijs) || 0 });

  // Alleen opslaan wanneer écht nodig (bv. publiceren in showroom vanuit de advertentiemodal)
  const ensureSaved = async () => {
    const payload = _payload();
    const saved = savedId ? await carsysApi.updatePart(savedId, payload) : await carsysApi.createPart(payload);
    if (saved?.id) setSavedId(saved.id);
    onSaved && onSaved();
    return saved?.id || savedId;
  };

  const maakAdvertentie = () => {
    if (!d.naam.trim()) { toast.error(t('Vul een onderdeelnaam in', 'Vul een onderdeelnaam in')); return; }
    setAdPart({ ..._payload(), id: savedId });
  };

  const [rdwBusy, setRdwBusy] = useState(false);
  const haalRdw = async () => {
    const kt = (d.kenteken || '').trim();
    if (!kt) { toast.error(t('Vul eerst een kenteken in', 'Vul eerst een kenteken in')); return; }
    setRdwBusy(true);
    try {
      const info = await carsysApi.rdwLookup(kt);
      if (!info || !info.merk) { toast.info(t('Geen RDW-gegevens gevonden voor dit kenteken', 'Geen RDW-gegevens gevonden voor dit kenteken')); return; }
      const jaar = info.datumEersteToelating ? String(info.datumEersteToelating).slice(0, 4) : '';
      const motor = [info.cilinderinhoud ? `${info.cilinderinhoud} cc` : '', info.aantalCilinders ? `${info.aantalCilinders} cil.` : ''].filter(Boolean).join(' · ');
      setD(p => ({
        ...p,
        merk: p.merk || info.merk || '',
        bouwjaar: p.bouwjaar || jaar,
        motor: p.motor || motor,
        afkomstigVoertuig: p.afkomstigVoertuig || [info.merk, info.handelsbenaming].filter(Boolean).join(' '),
      }));
      toast.success(`RDW: ${info.merk} ${info.handelsbenaming}`);
    } catch (e) {
      toast.error(t('RDW lookup mislukt', 'RDW lookup mislukt'), { description: e.response?.data?.detail || e.message });
    } finally { setRdwBusy(false); }
  };

  const [compatBusy, setCompatBusy] = useState(false);
  const [compatResult, setCompatResult] = useState(null);
  const checkCompat = async () => {
    if (!(d.naam || '').trim()) { toast.error(t('Vul eerst een onderdeelnaam in', 'Vul eerst een onderdeelnaam in')); return; }
    setCompatBusy(true); setCompatResult(null);
    try {
      const crossRefs = Array.from(new Set([...(d.oemCrossRef || []), crossInput.trim()].filter(Boolean)));
      const r = await carsysApi.partCompatibiliteit({
        naam: d.naam, oemNummer: d.oemNummer, onderdeelNummer: d.onderdeelNummer,
        oemCrossRef: crossRefs, afkomstigVoertuig: d.afkomstigVoertuig,
        merk: d.merk, aftermarketMerk: d.merk, model: d.model, bouwjaar: d.bouwjaar, motor: d.motor, pastOp: d.pastOp,
      });
      setCompatResult(r);
      if (!(r.voertuigen || []).length) toast.info(t('Geen extra voertuigen gevonden', 'Geen extra voertuigen gevonden'));
    } catch (e) {
      toast.error(t('AI-check mislukt', 'AI-check mislukt'), { description: e.response?.data?.detail || e.message });
    } finally { setCompatBusy(false); }
  };
  const voegCompatToe = (v) => {
    setD(p => {
      const bestaat = (p.pastOp || []).some(x => (x.merk || '').toLowerCase() === (v.merk || '').toLowerCase() && (x.model || '').toLowerCase() === (v.model || '').toLowerCase());
      if (bestaat) return p;
      return { ...p, pastOp: [...(p.pastOp || []), { merk: v.merk || '', model: v.model || '', bouwjaar: v.bouwjaar || '' }] };
    });
  };
  const voegAlleCompatToe = () => {
    (compatResult?.voertuigen || []).forEach(voegCompatToe);
    const extraOem = (compatResult?.oem_alternatieven || []).filter(Boolean);
    if (extraOem.length) setD(p => ({ ...p, oemCrossRef: Array.from(new Set([...(p.oemCrossRef || []), ...extraOem])) }));
    toast.success(t('Toegevoegd aan "Past op"', 'Toegevoegd aan "Past op"'));
  };

  // OEM cross-reference (lijst alternatieve nummers)
  const [crossInput, setCrossInput] = useState('');
  const addCross = () => {
    const v = crossInput.trim();
    if (!v) return;
    upd('oemCrossRef', [...(d.oemCrossRef || []), v]);
    setCrossInput('');
  };
  const removeCross = (i) => upd('oemCrossRef', (d.oemCrossRef || []).filter((_, idx) => idx !== i));

  // Past-op lijst (merk / model / bouwjaar)
  const addPastOp = () => upd('pastOp', [...(d.pastOp || []), { merk: '', model: '', bouwjaar: '' }]);
  const setPastOp = (i, k, v) => upd('pastOp', (d.pastOp || []).map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const removePastOp = (i) => upd('pastOp', (d.pastOp || []).filter((_, idx) => idx !== i));

  const maakHoofdfoto = (i) => {
    const fotos = [...(d.fotos || [])];
    const [f] = fotos.splice(i, 1);
    fotos.unshift(f);
    upd('fotos', fotos);
  };

  const onFotos = async (e) => {
    const files = Array.from(e.target.files || []).filter(f => /image\/(jpeg|png|webp)/.test(f.type));
    if (!files.length) return;
    const verkleind = await Promise.all(files.map(verkleinFoto));
    upd('fotos', [...(d.fotos || []), ...verkleind].slice(0, 10));
    if (fileRef.current) fileRef.current.value = '';
  };

  const save = async () => {
    if (!d.naam.trim()) { toast.error(t('Vul een onderdeelnaam in', 'Vul een onderdeelnaam in')); return; }
    setSaving(true);
    try {
      const payload = { ...d, aantal: Number(d.aantal) || 1, minVoorraad: Number(d.minVoorraad) || 0, garantieMaanden: Number(d.garantieMaanden) || 0, inkoopPrijs: Number(d.inkoopPrijs) || 0, vraagPrijs: Number(d.vraagPrijs) || 0, verkoopPrijs: Number(d.verkoopPrijs) || 0 };
      if (savedId) await carsysApi.updatePart(savedId, payload);
      else await carsysApi.createPart(payload);
      toast.success(t('Onderdeel opgeslagen', 'Onderdeel opgeslagen'));
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(t('Opslaan mislukt', 'Opslaan mislukt'), { description: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  return (
    <div className="cs-modal-overlay" onClick={requestClose} style={{ alignItems: 'flex-start', overflowY: 'auto', padding: '24px 16px' }}>
      <div className="cs-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 720, width: '96vw' }} data-testid="part-editor">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>{part?.id ? t('Onderdeel bewerken', 'Onderdeel bewerken') : t('Nieuw onderdeel', 'Nieuw onderdeel')}</h3>
          <button className="cs-btn ghost" onClick={requestClose} data-testid="pe-close"><X size={16} /></button>
        </div>

        <div style={{ display: 'flex', gap: 16, borderBottom: '1px solid #e2e8f0', marginBottom: 14, flexWrap: 'wrap' }}>
          {[['gegevens', t('Gegevens', 'Gegevens')], ['compat', t('Past op', 'Past op')], ['fotos', `${t('Foto\'s', 'Foto\'s')} (${(d.fotos || []).length})`]].map(([id, lbl]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`pe-tab-${id}`}
              style={{ padding: '6px 2px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13.5, fontWeight: 600, color: tab === id ? '#7c3aed' : '#64748b', borderBottom: tab === id ? '2px solid #7c3aed' : '2px solid transparent', marginBottom: -1 }}>{lbl}</button>
          ))}
        </div>

        {tab === 'gegevens' ? (
          <div data-testid="pe-gegevens">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 10 }}>
              <F label={t('Onderdeelnaam', 'Onderdeelnaam')} testid="pe-naam" value={d.naam} onChange={v => upd('naam', v)} />
              <Sel label={t('Categorie', 'Categorie')} testid="pe-categorie" value={d.categorie} onChange={v => upd('categorie', v)} opts={CATEGORIEEN} />
              <Sel label={t('Conditie', 'Conditie')} testid="pe-conditie" value={d.conditie} onChange={v => upd('conditie', v)} opts={CONDITIES} />
              <F label={t('Merk', 'Merk')} testid="pe-merk" value={d.merk} onChange={v => upd('merk', v)} />
              <F label={t('OEM nummer', 'OEM nummer')} testid="pe-oem" value={d.oemNummer} onChange={v => upd('oemNummer', v)} />
              <F label={t('Onderdeelnummer', 'Onderdeelnummer')} testid="pe-nr" value={d.onderdeelNummer} onChange={v => upd('onderdeelNummer', v)} />
              <div>
                <label style={lblStyle}>{t('Locatie in magazijn', 'Locatie in magazijn')}</label>
                <input className="cs-input" list="pe-locaties-list" value={d.magazijnLocatie ?? ''} onChange={e => upd('magazijnLocatie', e.target.value)} placeholder={locaties.length ? t('Kies of typ…', 'Kies of typ…') : t('Bv. Schap 3', 'Bv. Schap 3')} data-testid="pe-locatie" />
                <datalist id="pe-locaties-list">{locaties.map(l => <option key={l} value={l} />)}</datalist>
              </div>
              <F label={t('Aantal', 'Aantal')} testid="pe-aantal" type="number" value={d.aantal} onChange={v => upd('aantal', v)} />
              <F label={t('Min. voorraad', 'Min. voorraad')} testid="pe-minvoorraad" type="number" value={d.minVoorraad} onChange={v => upd('minVoorraad', v)} />
              <F label={t('Inkoopprijs', 'Inkoopprijs')} testid="pe-inkoop" type="number" value={d.inkoopPrijs} onChange={v => upd('inkoopPrijs', v)} />
              <F label={t('Vraagprijs', 'Vraagprijs')} testid="pe-vraag" type="number" value={d.vraagPrijs} onChange={v => upd('vraagPrijs', v)} />
              <F label={t('Verkoopprijs', 'Verkoopprijs')} testid="pe-verkoop" type="number" value={d.verkoopPrijs} onChange={v => upd('verkoopPrijs', v)} />
              <F label={t('Inkoopbron / leverancier', 'Inkoopbron / leverancier')} testid="pe-inkoopbron" value={d.inkoopbron} onChange={v => upd('inkoopbron', v)} />
              <F label={t('Garantie (maanden)', 'Garantie (maanden)')} testid="pe-garantie" type="number" value={d.garantieMaanden} onChange={v => upd('garantieMaanden', v)} />
              <Sel label={t('Retourstatus', 'Retourstatus')} testid="pe-retour" value={d.retourStatus} onChange={v => upd('retourStatus', v)} opts={RETOUR_STATUSSEN} />
              <F label={t('Demontagedatum', 'Demontagedatum')} testid="pe-demontage" type="date" value={d.demontageDatum} onChange={v => upd('demontageDatum', v)} />
              <F label={t('Afkomstig van voertuig', 'Afkomstig van voertuig')} testid="pe-voertuig" value={d.afkomstigVoertuig} onChange={v => upd('afkomstigVoertuig', v)} />
            </div>

            {/* AI prijsadvies — is de vraagprijs marktconform? */}
            <div style={{ marginTop: 14, padding: '12px 14px', background: '#fafaf9', border: '1px solid #e7e5e4', borderRadius: 8 }} data-testid="pe-prijsadvies">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color: '#7c3aed' }}>{t('AI prijsadvies', 'AI prijsadvies')}</div>
                <button className="cs-btn" onClick={haalPrijsAdvies} disabled={prijsBusy} data-testid="pe-prijsadvies-btn"
                  style={{ background: '#7c3aed', color: '#fff', borderColor: '#7c3aed', fontSize: 12.5 }}>
                  {prijsBusy ? <Loader2 size={14} className="spin" /> : <Sparkles size={14} />} {t('Bepaal marktprijs', 'Bepaal marktprijs')}
                </button>
              </div>
              {prijsAdvies && (() => {
                const mw = prijsAdvies.gebruikteMarktwaarde || {};
                const vraag = Number(d.vraagPrijs) || Number(d.verkoopPrijs) || 0;
                const min = Number(mw.min) || 0, max = Number(mw.max) || 0;
                let kleur = '#64748b', label = t('Stel een vraagprijs in', 'Stel een vraagprijs in');
                if (vraag > 0 && (min || max)) {
                  if (vraag < min) { kleur = '#2563eb'; label = t('Onder marktprijs — snelle verkoop', 'Onder marktprijs — snelle verkoop'); }
                  else if (vraag > max) { kleur = '#d97706'; label = t('Boven marktprijs — kan verkoop vertragen', 'Boven marktprijs — kan verkoop vertragen'); }
                  else { kleur = '#16a34a'; label = t('Marktconform', 'Marktconform'); }
                }
                const eur = (n) => `€ ${(Number(n) || 0).toLocaleString('nl-NL')}`;
                return (
                  <div style={{ marginTop: 10 }} data-testid="pe-prijsadvies-result">
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: kleur, color: '#fff', borderRadius: 999, padding: '4px 12px', fontSize: 12.5, fontWeight: 700 }} data-testid="pe-prijs-badge">
                      <span style={{ width: 8, height: 8, borderRadius: 99, background: '#fff', opacity: 0.9 }} /> {label}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 10, fontSize: 12.5 }}>
                      <span>{t('Marktwaarde gebruikt', 'Marktwaarde gebruikt')}: <b>{eur(min)} – {eur(max)}</b></span>
                      {prijsAdvies.adviesVraagprijs ? <span>{t('Adviesvraagprijs', 'Adviesvraagprijs')}: <b style={{ color: '#7c3aed' }}>{eur(prijsAdvies.adviesVraagprijs)}</b></span> : null}
                      {prijsAdvies.snelleVerkoopPrijs ? <span>{t('Snel verkopen', 'Snel verkopen')}: <b>{eur(prijsAdvies.snelleVerkoopPrijs)}</b></span> : null}
                    </div>
                    {prijsAdvies.adviesVraagprijs ? (
                      <button className="cs-btn" onClick={gebruikAdviesprijs} data-testid="pe-prijs-gebruik" style={{ marginTop: 10, fontSize: 12 }}>
                        <Plus size={13} /> {t('Gebruik adviesprijs als vraagprijs', 'Gebruik adviesprijs als vraagprijs')}
                      </button>
                    ) : null}
                    {prijsAdvies.toelichting && <div style={{ marginTop: 8, fontSize: 12, color: '#475569' }}>{prijsAdvies.toelichting}</div>}
                  </div>
                );
              })()}
            </div>

            {/* Donorvoertuig — voor RDW-info & betere advertentie */}
            <div style={{ marginTop: 14, padding: '12px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }} data-testid="pe-donor">
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color: '#7c3aed', marginBottom: 8 }}>{t('Donorvoertuig', 'Donorvoertuig')}</div>
              {sloopautos.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <label style={{ ...lblStyle, margin: '0 0 3px' }}><Car size={12} style={{ verticalAlign: 'middle' }} /> {t('Koppel sloopauto', 'Koppel sloopauto')}</label>
                  <select className="cs-input" value={d.sloopautoId || ''} onChange={e => koppelSloopauto(e.target.value)} data-testid="pe-sloopauto">
                    <option value="">{t('— Geen / handmatig —', '— Geen / handmatig —')}</option>
                    {sloopautos.map(s => (
                      <option key={s.id} value={s.id}>{[s.kenteken, s.merk, s.model, s.bouwjaar].filter(Boolean).join(' · ')}</option>
                    ))}
                  </select>
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 10, alignItems: 'end' }}>
                <div>
                  <label style={lblStyle}>{t('Kenteken', 'Kenteken')}</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input className="cs-input" value={d.kenteken ?? ''} onChange={e => upd('kenteken', e.target.value.toUpperCase())} placeholder="12-AB-34" data-testid="pe-kenteken" />
                    <button className="cs-btn" onClick={haalRdw} disabled={rdwBusy} title={t('Haal RDW-gegevens op', 'Haal RDW-gegevens op')} data-testid="pe-rdw-btn">RDW</button>
                  </div>
                </div>
                <F label={t('Kilometerstand', 'Kilometerstand')} testid="pe-kmstand" type="number" value={d.kmStand} onChange={v => upd('kmStand', v)} />
                <F label={t('Bouwjaar', 'Bouwjaar')} testid="pe-bouwjaar" value={d.bouwjaar} onChange={v => upd('bouwjaar', v)} />
                <F label={t('Motor / inhoud', 'Motor / inhoud')} testid="pe-motor" value={d.motor} onChange={v => upd('motor', v)} />
              </div>
            </div>
            <label style={lblStyle}>{t('Opmerkingen', 'Opmerkingen')}</label>
            <textarea className="cs-input" rows={3} value={d.opmerkingen} onChange={e => upd('opmerkingen', e.target.value)} data-testid="pe-opmerkingen" style={{ resize: 'vertical' }} />
          </div>
        ) : tab === 'compat' ? (
          <div data-testid="pe-compat">
            {/* OEM cross-reference */}
            <div style={{ marginBottom: 18 }}>
              <label style={{ ...lblStyle, marginTop: 0 }}>{t('OEM cross-reference (alternatieve nummers)', 'OEM cross-reference (alternatieve nummers)')}</label>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input className="cs-input" placeholder={t('Bijv. 06J115403Q', 'Bijv. 06J115403Q')} value={crossInput}
                  onChange={e => setCrossInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCross(); } }} data-testid="pe-cross-input" />
                <button className="cs-btn" onClick={addCross} data-testid="pe-cross-add"><Plus size={14} /> {t('Toevoegen', 'Toevoegen')}</button>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {(d.oemCrossRef || []).length === 0 && <span style={{ fontSize: 12.5, color: '#94a3b8' }}>{t('Nog geen alternatieve nummers', 'Nog geen alternatieve nummers')}</span>}
                {(d.oemCrossRef || []).map((c, i) => (
                  <span key={i} data-testid={`pe-cross-${i}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 999, padding: '4px 6px 4px 11px', fontSize: 12.5, fontFamily: 'ui-monospace, monospace', fontWeight: 600 }}>
                    {c}
                    <button onClick={() => removeCross(i)} data-testid={`pe-cross-del-${i}`} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#dc2626', display: 'flex', padding: 0 }}><X size={13} /></button>
                  </span>
                ))}
              </div>
            </div>

            {/* Past-op lijst */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8, flexWrap: 'wrap' }}>
              <label style={{ ...lblStyle, margin: 0 }}>{t('Past op voertuigen', 'Past op voertuigen')}</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="cs-btn" style={{ background: '#7c3aed', color: '#fff', borderColor: '#7c3aed' }} onClick={checkCompat} disabled={compatBusy} data-testid="pe-ai-compat">
                  {compatBusy ? <Loader2 size={14} className="spin" /> : <Sparkles size={14} />} {t('AI: waar past dit nog meer op?', 'AI: waar past dit nog meer op?')}
                </button>
                <button className="cs-btn" onClick={addPastOp} data-testid="pe-pastop-add"><Plus size={14} /> {t('Voertuig toevoegen', 'Voertuig toevoegen')}</button>
              </div>
            </div>

            {compatResult && (
              <div data-testid="pe-compat-result" style={{ marginBottom: 14, border: '1px solid #ddd6fe', background: '#faf5ff', borderRadius: 10, padding: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 800, color: '#6d28d9', textTransform: 'uppercase', letterSpacing: 0.5 }}><Sparkles size={12} /> {t('AI-suggesties', 'AI-suggesties')} ({(compatResult.voertuigen || []).length})</span>
                  {(compatResult.voertuigen || []).length > 0 && <button className="cs-btn" style={{ fontSize: 11, padding: '3px 8px' }} onClick={voegAlleCompatToe} data-testid="pe-compat-add-all">{t('Alles toevoegen', 'Alles toevoegen')}</button>}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {(compatResult.voertuigen || []).map((v, i) => {
                    const zk = { hoog: '#16a34a', gemiddeld: '#d97706', laag: '#94a3b8' }[(v.zekerheid || '').toLowerCase()] || '#94a3b8';
                    return (
                      <div key={i} data-testid={`pe-compat-item-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, background: '#fff', border: '1px solid #ede9fe', borderRadius: 8, padding: '6px 10px' }}>
                        <span style={{ width: 8, height: 8, borderRadius: 99, background: zk, flexShrink: 0 }} title={v.zekerheid} />
                        <span style={{ fontWeight: 600 }}>{v.merk} {v.model}</span>
                        <span style={{ color: '#64748b', fontSize: 12 }}>{[v.bouwjaar, v.motor].filter(Boolean).join(' · ')}</span>
                        <button className="cs-btn ghost" style={{ marginLeft: 'auto', fontSize: 11, padding: '2px 8px' }} onClick={() => { voegCompatToe(v); toast.success(t('Toegevoegd', 'Toegevoegd')); }} data-testid={`pe-compat-add-${i}`}><Plus size={12} /></button>
                      </div>
                    );
                  })}
                </div>
                {(compatResult.oem_alternatieven || []).length > 0 && (
                  <div style={{ marginTop: 8, fontSize: 12, color: '#475569' }}>{t('Mogelijke OEM-alternatieven', 'Mogelijke OEM-alternatieven')}: <span style={{ fontFamily: 'ui-monospace, monospace' }}>{compatResult.oem_alternatieven.join(', ')}</span></div>
                )}
                {compatResult.let_op && <div style={{ marginTop: 8, fontSize: 11.5, color: '#b45309', display: 'flex', gap: 5 }}><Info size={13} /> {compatResult.let_op}</div>}
              </div>
            )}
            {(d.pastOp || []).length === 0 && <div style={{ fontSize: 12.5, color: '#94a3b8' }}>{t('Voeg merken/modellen toe waar dit onderdeel op past.', 'Voeg merken/modellen toe waar dit onderdeel op past.')}</div>}
            <div className="space-y-2">
              {(d.pastOp || []).map((r, i) => (
                <div key={i} data-testid={`pe-pastop-${i}`} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 110px 36px', gap: 8, alignItems: 'center' }}>
                  <input className="cs-input" placeholder={t('Merk', 'Merk')} value={r.merk || ''} onChange={e => setPastOp(i, 'merk', e.target.value)} data-testid={`pe-pastop-merk-${i}`} />
                  <input className="cs-input" placeholder={t('Model', 'Model')} value={r.model || ''} onChange={e => setPastOp(i, 'model', e.target.value)} data-testid={`pe-pastop-model-${i}`} />
                  <input className="cs-input" placeholder={t('Bouwjaar', 'Bouwjaar')} value={r.bouwjaar || ''} onChange={e => setPastOp(i, 'bouwjaar', e.target.value)} data-testid={`pe-pastop-jaar-${i}`} />
                  <button onClick={() => removePastOp(i)} data-testid={`pe-pastop-del-${i}`} style={{ width: 34, height: 34, border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', color: '#dc2626', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div data-testid="pe-fotos">
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={onFotos} style={{ display: 'none' }} data-testid="pe-foto-input" />
            <button className="cs-btn" onClick={() => fileRef.current?.click()} disabled={(d.fotos || []).length >= 10} data-testid="pe-foto-upload"
              style={{ width: '100%', justifyContent: 'center', padding: 14, border: '2px dashed #cbd5e1', background: '#f8fafc', marginBottom: 12 }}>
              <Upload size={16} /> {t('Foto\'s uploaden', 'Foto\'s uploaden')} ({(d.fotos || []).length}/10)
            </button>
            <button className="cs-btn" onClick={fotoAnalyse} disabled={fotoBusy || !(d.fotos || []).length} data-testid="pe-foto-analyse"
              style={{ width: '100%', justifyContent: 'center', padding: 12, background: '#7c3aed', color: '#fff', borderColor: '#7c3aed', marginBottom: 12 }}>
              {fotoBusy ? <Loader2 size={16} className="spin" /> : <Wand2 size={16} />} {t('AI: herken onderdeel uit foto\'s', 'AI: herken onderdeel uit foto\'s')}
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: '#64748b', background: '#eff6ff', padding: '8px 10px', borderRadius: 8, marginBottom: 12 }}>
              <Info size={14} /> {t('De eerste foto is de hoofdfoto. Klik op de ster om een andere foto als hoofdfoto in te stellen.', 'De eerste foto is de hoofdfoto. Klik op de ster om een andere foto als hoofdfoto in te stellen.')}
            </div>
            {(d.fotos || []).length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8 }}>
                {d.fotos.map((src, i) => (
                  <div key={i} style={{ position: 'relative', aspectRatio: '4/3', borderRadius: 8, overflow: 'hidden', background: '#e2e8f0', outline: i === 0 ? '2px solid #7c3aed' : 'none' }} data-testid={`pe-foto-${i}`}>
                    <img src={src} alt={`foto ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    {i === 0 && <span style={{ position: 'absolute', top: 4, left: 4, background: '#7c3aed', color: '#fff', fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 5 }} data-testid={`pe-foto-hoofd-${i}`}>{t('Hoofd', 'Hoofd')}</span>}
                    {i !== 0 && <button onClick={() => maakHoofdfoto(i)} data-testid={`pe-foto-star-${i}`} title={t('Maak hoofdfoto', 'Maak hoofdfoto')}
                      style={{ position: 'absolute', top: 4, left: 4, width: 24, height: 24, borderRadius: 6, border: 'none', background: 'rgba(124,58,237,.9)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Star size={13} /></button>}
                    <button onClick={() => upd('fotos', d.fotos.filter((_, idx) => idx !== i))} data-testid={`pe-foto-del-${i}`}
                      style={{ position: 'absolute', top: 4, right: 4, width: 24, height: 24, borderRadius: 6, border: 'none', background: 'rgba(220,38,38,.92)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Trash2 size={13} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
          <button className="cs-btn" onClick={maakAdvertentie} disabled={saving} data-testid="pe-maak-advertentie"
            style={{ background: '#a855f7', color: '#fff', borderColor: '#a855f7' }}>
            <Megaphone size={14} /> {t('Advertentie maken (AI)', 'Advertentie maken (AI)')}
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="cs-btn" onClick={requestClose} data-testid="pe-cancel">{t('Annuleren', 'Annuleren')}</button>
            <button className="cs-btn primary" onClick={save} disabled={saving} data-testid="pe-save"><Save size={14} /> {t('Opslaan', 'Opslaan')}</button>
          </div>
        </div>
      </div>
      {adPart && <PartAdvertentie part={adPart} onClose={() => setAdPart(null)} onNeedSave={ensureSaved} onUseText={(txt) => { upd('opmerkingen', txt); setAdPart(null); toast.success(t('Advertentietekst in opmerkingen gezet', 'Advertentietekst in opmerkingen gezet'), { description: t('Klik op Opslaan om te bewaren.', 'Klik op Opslaan om te bewaren.') }); }} />}
    </div>
  );
}

const lblStyle = { fontSize: 12, color: 'var(--cs-text-muted)', fontWeight: 600, display: 'block', margin: '12px 0 3px' };
const F = ({ label, value, onChange, testid, type = 'text' }) => (
  <div><label style={lblStyle}>{label}</label><input className="cs-input" type={type} value={value ?? ''} onChange={e => onChange(e.target.value)} data-testid={testid} /></div>
);
const Sel = ({ label, value, onChange, testid, opts }) => (
  <div><label style={lblStyle}>{label}</label>
    <select className="cs-input" value={value || ''} onChange={e => onChange(e.target.value)} data-testid={testid}>
      <option value="">—</option>
      {opts.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);
